import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../styles/MarketingTheme.css';


import MarketingHeader from '../components/MarketingHeader';

function FeaturesPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [expandedFeature, setExpandedFeature] = useState('qr-ordering');

    useEffect(() => {
        // Check if there's a feature parameter in the URL
        const featureParam = searchParams.get('feature');
        if (featureParam) {
            setExpandedFeature(featureParam);
            // Scroll to the feature after a short delay to ensure rendering
            setTimeout(() => {
                const element = document.getElementById(`feature-${featureParam}`);
                if (element) {
                    const headerOffset = 100;
                    const elementPosition = element.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                    window.scrollTo({
                        top: offsetPosition,
                        behavior: "smooth"
                    });
                }
            }, 300);
        } else {
            // If no feature param, scroll to top
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [searchParams]);

    const features = [
        {
            id: 'qr-ordering',
            title: 'QR Code Ordering',
            icon: 'fa-qrcode',
            color: 'purple',
            tagline: 'Frictionless ordering at customers\' fingertips',
            image: '/qr_ordering_feature_1765608231179.png',
            description: 'Customers scan a QR code at their table to instantly access your digital menu, browse items, customize orders, and pay—all from their own devices.',
            benefits: [
                'No app download required',
                'Instant menu access via QR code',
                'Real-time order tracking',
                'Multiple payment options',
                'Contactless and hygienic',
                'Reduces staff workload by 40%'
            ],
            howItWorks: [
                'Customer scans QR code on table',
                'Digital menu loads instantly on their phone',
                'Browse categories, view images & descriptions',
                'Add items to cart with customizations',
                'Confirm order with one tap',
                'Payment processed securely',
                'Order sent directly to kitchen'
            ]
        },
        {
            id: 'kds',
            title: 'Kitchen Display System',
            icon: 'fa-fire',
            color: 'orange',
            tagline: 'Replace printers with smart screens',
            image: '/dash-feature-kds-new.png',
            description: 'A modern, real-time kitchen display that replaces paper tickets. Orders appear instantly with clear preparation instructions, timers, and status updates.',
            benefits: [
                'Eliminate paper waste',
                'Real-time order synchronization',
                'Color-coded priority indicators',
                'Built-in preparation timers',
                'One-tap status updates',
                'Reduces order errors by 60%'
            ],
            howItWorks: [
                'Orders appear instantly on KDS screen',
                'Color-coded: Red (new), Yellow (preparing), Green (ready)',
                'Chef taps "Start" to begin preparation',
                'Timer tracks preparation time',
                'Mark items as "Ready" when complete',
                'Historical data for analytics'
            ]
        },
        {
            id: 'analytics',
            title: 'Analytics & Insights',
            icon: 'fa-chart-line',
            color: 'green',
            tagline: 'Data-driven decisions for growth',
            image: '/analytics_dashboard_feature_1765608276694.png',
            description: 'Comprehensive analytics dashboard with sales trends, popular items, peak hours, customer behavior, and actionable business insights.',
            benefits: [
                'Real-time sales tracking',
                'Top-selling items analysis',
                'Peak hours identification',
                'Revenue trend forecasting',
                'Customer behavior insights',
                'Exportable reports (PDF, Excel)'
            ],
            howItWorks: [
                'Automatic data collection from all orders',
                'Daily, weekly, monthly reporting',
                'Visual charts and graphs',
                'Filter by date range, branch, item',
                'Identify best-sellers and slow movers',
                'Download reports for accounting',
                'AI-powered recommendations'
            ]
        },
        {
            id: 'table-management',
            title: 'Table Management',
            icon: 'fa-th',
            color: 'blue',
            tagline: 'Digital floor plan with real-time status',
            image: '/table_management_feature_1765608294874.png',
            description: 'Visual floor plan showing table occupancy, reservations, and real-time status. Optimize seating and reduce wait times.',
            benefits: [
                'Real-time table status',
                'Visual floor plan',
                'Occupancy tracking',
                'Wait time optimization',
                'Increases table turnover by 20%'
            ],
            howItWorks: [
                'Create digital floor plan',
                'Add tables with numbers and capacities',
                'Track status: Available, Occupied, Reserved',
                'See how long customers have been seated',
                'Manage reservations',
                'Assign orders to specific tables',
                'Auto-release tables after payment'
            ]
        },
        {
            id: 'menu-management',
            title: 'Dynamic Menu Control',
            icon: 'fa-book-open',
            color: 'indigo',
            tagline: 'Update your menu in seconds, live everywhere',
            image: '/dash-feature-menu-v2.png',
            description: 'Centralized menu management with instant updates across all channels. Add, edit, or remove items in real-time.',
            benefits: [
                'Instant menu updates',
                'Rich media support (images, videos)',
                'Category organization',
                'Availability toggles',
                'Price management',
                'Multi-language support'
            ],
            howItWorks: [
                'Admin logs into dashboard',
                'Add/edit menu items with images',
                'Set prices, descriptions, allergens',
                'Organize into categories',
                'Toggle availability (in-stock/out-of-stock)',
                'Changes reflect instantly on customer app',
            ]
        },
        {
            id: 'inventory',
            title: 'Smart Inventory',
            icon: 'fa-cubes',
            color: 'teal',
            tagline: 'Automated stock tracking with alerts',
            image: '/inventory_management_feature_1765608351609.png',
            description: 'Intelligent inventory system that tracks ingredients, auto-deducts stock on orders, and alerts you when supplies run low.',
            benefits: [
                'Low-stock alerts',
                'Ingredient tracking',
                'Reduces food waste by 30%'
            ],
            howItWorks: [
                'Set stock levels and reorder points',
                'Receive alerts when inventory is low',
            ]
        },
        {
            id: 'role-management',
            title: 'Role & Permission Management',
            icon: 'fa-user-shield',
            color: 'purple',
            tagline: 'Secure access control for your team',
            image: '/role_management_feature_1765608373048.png',
            description: 'Granular role-based access control. Define permissions for staff, chefs, managers, and admins with secure authentication.',
            benefits: [
                'Custom role creation',
                'Granular permission control',
                'Secure login system',
                'Activity logging',
                'Multi-branch support',
                'Enhanced security & accountability'
            ],
            howItWorks: [
                'Create custom roles (e.g., Waiter, Chef, Manager)',
                'Assign permissions per role',
                'Users login with unique credentials',
                'Access only permitted features',
                'Track user activity and changes',
                'Manage staff across multiple branches',
                'Revoke access instantly if needed'
            ]
        },
        {
            id: 'branch-management',
            title: 'Branch Management',
            icon: 'fa-code-branch',
            color: 'red',
            tagline: 'Manage multiple locations from one dashboard',
            image: '/dash-feature-branches-new.png',
            description: 'Comprehensive multi-location management system. Control menus, inventory, staff, and operations across all your restaurant branches from a single unified dashboard.',
            benefits: [
                'Centralized multi-branch control',
                'Branch-specific menu customization',
                'Individual inventory tracking per location',
                'Cross-branch analytics and reporting',
                'Staff assignment to specific branches',
                'Real-time sync across all locations'
            ],
            howItWorks: [
                'Create and configure multiple branch locations',
                'Assign unique settings to each branch',
                'Customize menus per branch if needed',
                'Track inventory separately for each location',
                'View consolidated or branch-specific reports',
                'Assign staff and roles to specific branches',
                'Monitor all branches in real-time from one dashboard'
            ]
        },
        {
            id: 'integrations',
            title: 'Third-Party Integrations',
            icon: 'fa-project-diagram',
            color: 'orange',
            tagline: 'Connect with your favorite delivery platforms',
            image: 'https://images.unsplash.com/photo-1526367790999-015070c23f5d?q=80&w=2070&auto=format&fit=crop',
            description: 'Streamline your delivery operations by integrating directly with major third-party platforms. Manage all orders from a single interface.',
            benefits: [
                'Unified order management',
                'Automatic menu synchronization',
                'Real-time status updates',
                'Consolidated analytics',
                'Reduced operational complexity'
            ],
            howItWorks: [
                'Connect Swiggy & Zomato merchant accounts',
                'Map RedSorm menu items to external platforms',
                'Orders appear instantly on your RedSorm KDS',
                'Update item availability globally with one tap',
                'View consolidated sales reports across all channels',
                'Sync stock levels in real-time (Coming Soon)'
            ]
        },
        {
            id: 'loyalty-rewards',
            title: 'Rewards & Loyalty System',
            icon: 'fa-gift',
            color: 'teal',
            tagline: 'Keep customers coming back with custom rewards',
            image: '/rewards_system_feature.png',
            description: 'A fully integrated rewards and loyalty program. Configure points calculation rules, reward thresholds, manage customer accounts, and set up automatic birthday discount offers directly from your admin panel.',
            benefits: [
                'Increases customer retention by 35%',
                'Flexible points calculation rules',
                'Automatic birthday discounts and birthday celebrations triggers',
                'Real-time point balance lookup for customers',
                'Seamless checkout integration with quick redemption',
                'Detailed customer spending and loyalty reports'
            ],
            howItWorks: [
                'Admin configures reward rules and birthday discount values in dashboard',
                'Customers register birth date and phone number to join the program',
                'System automatically calculates birthday eligibility and awards points on checkouts',
                'Redeem points or birthday discounts instantly on checkout',
                'Track points and view profiles via QR code interface',
                'View detailed loyalty and discount impact analytics'
            ]
        },
        {
            id: 'manual-ordering',
            title: 'Manual Ordering & Entry',
            icon: 'fa-keyboard',
            color: 'green',
            tagline: 'Log all external orders directly into KDS',
            image: '/manual_ordering_feature.png',
            description: 'Log and track orders from external sources such as Swiggy, Zomato, or phone calls directly into your Kitchen Display System (KDS) for a consolidated kitchen queue.',
            benefits: [
                'Consolidate all order queues in one screen',
                'Quick-add items and select delivery platforms',
                'Log customer details and special delivery instructions',
                'Reduce cooking delays with unified timers',
                'Clean analytics reports merging offline & online revenue'
            ],
            howItWorks: [
                'Chef/Cashier opens KDS and clicks "Add Online Order"',
                'Select delivery platform (Swiggy, Zomato, Phone)',
                'Select menu items, add notes, and save',
                'Order is added directly to KDS queue with platform branding',
                'Kitchen prepares consolidated food items in sequence',
                'Analytics automatically counts revenue from all platforms'
            ]
        }
    ];

    const toggleFeature = (featureId) => {
        setExpandedFeature(expandedFeature === featureId ? null : featureId);
    };

    return (
        <div className="marketing-page stunning-theme">
            <MarketingHeader />

            {/* Hero Section */}
            <section className="marketing-section marketing-section-hero">
                <div className="marketing-container text-center">
                    <div className="marketing-section-badge">Comprehensive Features</div>
                    <h1 className="marketing-hero-title">
                        Everything You Need to<br />
                        <span className="marketing-hero-gradient">
                            Run a Modern Restaurant
                        </span>
                    </h1>
                    <p className="marketing-hero-description mx-auto">
                        From QR ordering to analytics, RedSorm provides a complete suite of tools designed to streamline operations and delight customers.
                    </p>
                </div>
            </section>

            {/* Features List */}
            <section className="marketing-section marketing-section-dark">
                <div className="marketing-container">
                    <div className="marketing-container-wide space-y-8">
                        {features.map((feature, index) => (
                            <div
                                key={feature.id}
                                id={`feature-${feature.id}`}
                                className={`marketing-card transition-all duration-500 ${expandedFeature === feature.id ? 'ring-1 ring-purple-500' : ''}`}
                            >
                                {/* Feature Header */}
                                <div
                                    onClick={() => toggleFeature(feature.id)}
                                    className="cursor-pointer flex items-center justify-between p-2 rounded-xl hover:bg-white/5 transition-colors"
                                >
                                    <div className="flex items-center space-x-6 flex-1">
                                        <div
                                            className="marketing-card-icon mb-0"
                                            style={{
                                                /* Using inline styles for dynamic colors based on feature.color */
                                                background: `linear-gradient(135deg, ${feature.color === 'purple' ? '#9333EA' : feature.color === 'orange' ? '#F97316' : feature.color === 'green' ? '#10B981' : feature.color === 'blue' ? '#3B82F6' : feature.color === 'indigo' ? '#6366F1' : '#14B8A6'} 0%, ${feature.color === 'purple' ? '#7E22CE' : feature.color === 'orange' ? '#EA580C' : feature.color === 'green' ? '#059669' : feature.color === 'blue' ? '#2563EB' : feature.color === 'indigo' ? '#4F46E5' : '#0F766E'} 100%)`,
                                                color: 'white',
                                                width: '80px', height: '80px'
                                            }}
                                        >
                                            <i className={`fas ${feature.icon} text-3xl`}></i>
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h2 className="text-3xl font-bold text-gray-100">{feature.title}</h2>
                                                {feature.id === 'integrations' && (
                                                    <span className="bg-purple-600/30 text-purple-400 text-xs font-bold px-2 py-1 rounded border border-purple-500/30">COMING SOON</span>
                                                )}
                                            </div>
                                            <p className="text-lg text-gray-400">{feature.tagline}</p>
                                        </div>
                                    </div>
                                    <div className="text-gray-400">
                                        <i className={`fas fa-chevron-down text-2xl transform transition-transform ${expandedFeature === feature.id ? 'rotate-180' : ''}`}></i>
                                    </div>
                                </div>

                                {/* Expanded Content */}
                                {expandedFeature === feature.id && (
                                    <div className="mt-8 pt-8 border-t border-white/10 marketing-animate-fade-in">
                                        <div className="grid md:grid-cols-2 gap-12">
                                            {/* Left: Image */}
                                            <div className="order-2 md:order-1">
                                                <div className="bg-gray-800/50 p-2 rounded-2xl shadow-inner border border-white/5">
                                                    <img
                                                        src={feature.image}
                                                        alt={feature.title}
                                                        className="w-full rounded-xl shadow-2xl opacity-90 hover:opacity-100 transition-opacity"
                                                    />
                                                </div>
                                            </div>

                                            {/* Right: Details */}
                                            <div className="order-1 md:order-2 space-y-8">
                                                <div>
                                                    <h3 className="text-2xl font-bold text-white mb-4">Overview</h3>
                                                    <p className="text-gray-300 text-lg leading-relaxed">{feature.description}</p>
                                                </div>

                                                <div>
                                                    <h3 className="text-2xl font-bold text-white mb-4">Key Benefits</h3>
                                                    <ul className="space-y-4">
                                                        {feature.benefits.map((benefit, i) => (
                                                            <li key={i} className="flex items-start">
                                                                <i className="fas fa-check-circle mr-3 mt-1.5" style={{ color: '#a855f7' }}></i>
                                                                <span className="text-gray-300 font-medium text-lg">{benefit}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>

                                        {/* How It Works */}
                                        <div className="mt-12 bg-white/5 rounded-2xl p-8 border border-white/5">
                                            <h3 className="text-2xl font-bold text-white mb-8 text-center">How It Works</h3>
                                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                                {feature.howItWorks.map((step, i) => (
                                                    <div key={i} className="flex items-start space-x-4 bg-gray-900/50 p-6 rounded-xl border border-white/5 hover:border-purple-500/30 transition-colors">
                                                        <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 shadow-lg text-white"
                                                            style={{ background: 'var(--gradient-primary)' }}>
                                                            {i + 1}
                                                        </div>
                                                        <p className="text-gray-300 font-medium leading-relaxed">{step}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="marketing-section">
                <div className="marketing-container">
                    <div className="marketing-cta">
                        <h2 className="marketing-cta-title">Ready to Experience All Features?</h2>
                        <p className="marketing-cta-description">
                            Start your 20-day free trial and discover how RedSorm can transform your restaurant operations.
                        </p>
                        <div className="flex-center gap-md">
                            <button
                                onClick={() => navigate('/signup')}
                                className="marketing-btn marketing-btn-large bg-white text-purple-600 hover:bg-gray-100"
                            >
                                Start Free Trial
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="marketing-footer">
                <div className="marketing-footer-content">
                    <p>&copy; {new Date().getFullYear()} RedSorm. All rights reserved.</p>
                    <div className="marketing-footer-links">
                        <a href="/terms-of-service" className="marketing-footer-link">Terms of Service</a>
                        <a href="/privacy-policy" className="marketing-footer-link">Privacy Policy</a>
                        <a href="/cookie-policy" className="marketing-footer-link">Cookie Policy</a>
                    </div>
                </div>
            </footer>

        </div>
    );
}

export default FeaturesPage;
